import { initSlide, SlideSection } from "@NextSkyTheme/slide";

function isPairedTestimonialImagesSection(section) {
  if (!section) return false;
  const mediaCount = section.querySelectorAll("[data-testimonial-images-media]").length;
  const contentCount = section.querySelectorAll("[data-testimonial-images-content]").length;
  return mediaCount === 1 && contentCount === 1;
}

function getSlideCount(swiper) {
  if (!swiper || swiper.destroyed) return 0;
  if (swiper.params?.loop) {
    return swiper.slides?.length
      ? swiper.slides.filter((slide) => !slide.classList.contains("swiper-slide-duplicate"))
          .length
      : 0;
  }
  return swiper.slides?.length || 0;
}

function clampSlideIndex(swiper, index) {
  const count = getSlideCount(swiper);
  if (!count) return 0;
  return Math.min(Math.max(Number(index) || 0, 0), count - 1);
}

class TestimonialImagesMedia extends HTMLElement {
  connectedCallback() {
    this.initFrame = requestAnimationFrame(() => {
      this.initFrame = null;
      if (!this.shouldSelfInit()) return;
      if (!this.swiper || this.swiper.destroyed) {
        initSlide(this);
        this.selfInited = true;
      }
    });
  }

  disconnectedCallback() {
    if (this.initFrame != null) {
      cancelAnimationFrame(this.initFrame);
      this.initFrame = null;
    }

    if (this.selfInited && this.swiper && !this.swiper.destroyed) {
      this.swiper.destroy(true, true);
    }

    this.swiper = null;
    this.selfInited = false;
  }

  shouldSelfInit() {
    return !isPairedTestimonialImagesSection(this.closest("[data-testimonial-images-section]"));
  }
}

class TestimonialImagesSlide extends SlideSection {
  connectedCallback() {
    this.initFrame = requestAnimationFrame(() => {
      this.initFrame = null;
      if (!this.swiper || this.swiper.destroyed) {
        super.init();
      }

      this.initMedia();
    });
  }

  disconnectedCallback() {
    if (this.initFrame != null) {
      cancelAnimationFrame(this.initFrame);
      this.initFrame = null;
    }

    this.clearSyncLock();
    this.unbindSync();

    if (this.ownsMediaSwiper && this.mediaSwiper && !this.mediaSwiper.destroyed) {
      this.mediaSwiper.destroy(true, true);
    }

    if (this.swiper && !this.swiper.destroyed) {
      this.swiper.destroy(true, true);
    }

    this.swiper = null;
    this.mediaSwiper = null;
    this.ownsMediaSwiper = false;
  }

  initMedia() {
    if (this.mediaSwiper || !this.swiper) return;

    const section = this.closest("[data-testimonial-images-section]");
    if (!isPairedTestimonialImagesSection(section)) return;

    const media = section.querySelector("[data-testimonial-images-media]");
    if (!media) return;

    const alreadyReady = media.swiper && !media.swiper.destroyed;
    this.mediaSwiper = alreadyReady ? media.swiper : initSlide(media);
    this.ownsMediaSwiper = !alreadyReady;
    this.syncSlides();
  }

  unbindSync() {
    if (this.swiper && !this.swiper.destroyed && this.onContentSlideChange) {
      this.swiper.off("slideChange", this.onContentSlideChange);
    }

    if (this.mediaSwiper && !this.mediaSwiper.destroyed && this.onMediaSlideChange) {
      this.mediaSwiper.off("slideChange", this.onMediaSlideChange);
    }

    if (this.swiper && !this.swiper.destroyed && this.onSyncLockEnd) {
      this.swiper.off("slideChangeTransitionEnd", this.onSyncLockEnd);
    }

    if (this.mediaSwiper && !this.mediaSwiper.destroyed && this.onSyncLockEnd) {
      this.mediaSwiper.off("slideChangeTransitionEnd", this.onSyncLockEnd);
    }

    this.onContentSlideChange = null;
    this.onMediaSlideChange = null;
    this.onSyncLockEnd = null;
  }

  clearSyncLock() {
    if (this.syncLockTimer != null) {
      clearTimeout(this.syncLockTimer);
      this.syncLockTimer = null;
    }
    this.isSyncing = false;
  }

  lockSync(swiper) {
    this.isSyncing = true;

    if (this.syncLockTimer != null) {
      clearTimeout(this.syncLockTimer);
    }

    const speed = Number(swiper?.params?.speed) || 300;
    this.syncLockTimer = setTimeout(() => {
      this.syncLockTimer = null;
      this.isSyncing = false;
    }, speed + 50);
  }

  syncSlides() {
    this.unbindSync();

    this.onSyncLockEnd = () => {
      this.clearSyncLock();
    };

    this.onContentSlideChange = () => {
      if (this.isSyncing) return;
      this.goToSlide(this.mediaSwiper, this.swiper.realIndex);
    };

    this.onMediaSlideChange = () => {
      if (this.isSyncing) return;
      this.goToSlide(this.swiper, this.mediaSwiper.realIndex);
    };

    this.swiper.on("slideChange", this.onContentSlideChange);
    this.mediaSwiper.on("slideChange", this.onMediaSlideChange);
    this.swiper.on("slideChangeTransitionEnd", this.onSyncLockEnd);
    this.mediaSwiper.on("slideChangeTransitionEnd", this.onSyncLockEnd);

    this.goToSlide(this.mediaSwiper, this.swiper.realIndex);
  }

  goToSlide(swiper, index) {
    if (!swiper || swiper.destroyed) return;

    const nextIndex = clampSlideIndex(swiper, index);
    if (swiper.realIndex === nextIndex) return;

    this.lockSync(swiper);

    try {
      if (swiper.params.loop) {
        swiper.slideToLoop(nextIndex);
      } else {
        swiper.slideTo(nextIndex);
      }
    } catch (_) {
      this.clearSyncLock();
    }
  }
}

if (!customElements.get("testimonial-images-media")) {
  customElements.define("testimonial-images-media", TestimonialImagesMedia);
}

if (!customElements.get("testimonial-images-slide")) {
  customElements.define("testimonial-images-slide", TestimonialImagesSlide);
}
